import React, { useState, useEffect, useRef } from 'react';
import ModelTrackerPoseEditor from './ModelTrackerPoseEditor';
import HoloLensVisorOverlay from './HoloLensVisorOverlay';
import Button from '../layout/Button';
import Spacer from '../layout/Spacer';
import ModalManager from '../layout/ModalManager';
import { SimulationStates } from '../../globals';
import WorkspaceManager from '../../classes/WorkspaceManager';
import { useTypedSelector } from "../../state/hooks";
import { GrapesDeviceTypes, IDisplayDeviceInfo } from "../../interfaces/VisageUI";
import ARTracker from "../../classes/ARTracker";
import { getUuidv4 } from '../../classes/utilities/Misc';

interface ModelTrackerCameraPreviewProps {
	hideEditButton?: boolean;
	simulationState?: SimulationStates;
	readOnly?: boolean;
	contained?: boolean;
	tracker: ARTracker;
}

export default function ModelTrackerCameraPreview(props: ModelTrackerCameraPreviewProps)
{
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const selfRef = useRef(null);
	const componentGuid = getUuidv4();
	const defaultHeight = 180;
	let editPoseModalId = "";

	const currentStepId = useTypedSelector(state => state.scenarioModule.currentStepId);
	const currentDisplayDevice: IDisplayDeviceInfo = useTypedSelector(state => state.visageModule.currentDisplayDevice);
	const [canvasHeight, setCanvasHeight] = useState<number>(0);
	const [canvasWidth, setCanvasWidth] = useState<number>(0);
	const [previewAspectName, setPreviewAspectName] = useState<string>("");

	useEffect(() =>
	{
		const aspectRatio = currentDisplayDevice.resolution.width / currentDisplayDevice.resolution.height;
		setPreviewAspectName(currentDisplayDevice.aspect);
		if(props.contained)
		{
			const parentElement = document.getElementById('trackersUserDefinedPoseContainer');
			if(parentElement)
			{
				setCanvasHeight(parentElement.clientHeight);
				setCanvasWidth(parentElement.clientHeight * aspectRatio);
			}
		}
		else
		{
			setCanvasHeight(defaultHeight);
			setCanvasWidth(defaultHeight * aspectRatio);
		}
		props.tracker.modelTrackerCamera.userData.renderIsDirty = true;		
		WorkspaceManager.setModelTrackerPreviewRenderData(componentGuid, { id: componentGuid, tracker: props.tracker, camera: props.tracker.modelTrackerCamera, context: canvasRef.current!.getContext("2d") });

		return () => 
		{
			WorkspaceManager.clearModelTrackerPreviewRenderData(componentGuid);
		}

	}, [currentStepId, currentDisplayDevice, props.tracker, document.getElementById('trackersUserDefinedPoseContainer')?.clientWidth]);

	function getEditPoseModalId()
	{
		return editPoseModalId;
	}

	function editPoseClicked()
	{
		editPoseModalId = ModalManager.addModal({
			showBackdrop: true,
			allowClickOut: false,
			content: <ModelTrackerPoseEditor
				tracker={props.tracker}
				getEditPoseModalId={getEditPoseModalId}/>,
		});
	}

	return (
		<div>
			{
				props.hideEditButton ? <></> :
					<>
						<Spacer size={{ y: "10px" }} />
					Device Pose Preview ({previewAspectName}):
						<Spacer size={{ y: "10px" }} />
					</>
			}
			<div className={props.hideEditButton ? "uipreviewmodeltracker" : "layoutPanel horizontal centerContent"}
				ref={selfRef}>
				<canvas
					ref={canvasRef}
					width={canvasWidth}
					height={canvasHeight}
					style={{
						width: canvasWidth+'px',
						height: canvasHeight+'px',
						border: props.simulationState === SimulationStates.Playing ? '0px' : props.hideEditButton ? '0px solid #000' : '1px solid #000',
						opacity: props.readOnly ? '50%' : '100%'
					}}
				/>
				{ 
					!props.hideEditButton && currentDisplayDevice.id === GrapesDeviceTypes.HoloLens2 && 
						<HoloLensVisorOverlay 
							// size and position calculated by hand to look good since the canvas aspect is set by the camera and not the display device
							// see https://scopearcloud.atlassian.net/browse/WC20-4880 for more details
							overlayWidth={153} 
							overlayHeight={82}
							verticalOffset={15}
						/> 
				}
			</div>
			{
				props.hideEditButton ? <></> :
					<>
						<Spacer size={{ y: "10px" }} />
						<div className={"layoutPanel horizontal centerContent"}>
							<Button data-name={"edit-pose"} label={"Edit Pose"} onClick={editPoseClicked} disabled={props.readOnly}/>
						</div>
						<Spacer size={{ y: "10px" }} />
					</>
			}
		</div>

	);
}

